
import { Component, Prop, Vue, Watch } from 'vue-property-decorator'
import { Table } from 'element-ui'
import { MultiChoiceCard, DotationOperation, MultiChoiceGiftCard, GiftCard, Gift } from '@/models'
import moment from 'moment'

import Pagination from '@/components/Pagination/index.vue'
import { clipboardSuccess } from '@/utils/clipboard'

interface TableRow {
  row: MultiChoiceCard;
}

@Component({
  name: 'MultiChoiceCardsTable',
  components: {
    Pagination,
    MultiChoiceCardDialog: () => import('./MultiChoiceCardDialog.vue'),
    ExtendMultiChoiceCardsExpirationDialog: () => import('./ExtendMultiChoiceCardsExpirationDialog.vue'),
    RevokeMultiChoiceCardDialog: () => import('./RevokeMultiChoiceCardDialog.vue'),
    ResendGiftEmailDialog: () => import('@/views/gifts/components/ResendGiftEmailDialog.vue'),
    RevokeMultiChoiceCardsDialog: () => import('./RevokeMultiChoiceCardsDialog.vue'),
    FiltersTable: () => import('./FiltersTable.vue'),
    DotationCampaignsUploadForm: () => import('./UploadForm/index.vue')
  }
})
export default class extends Vue {
  @Prop({ required: true }) readonly selectedOperation!: DotationOperation
  @Prop({ required: true }) private operation!: DotationOperation

  multiChoiceCards: MultiChoiceCard[] = []
  totalMultiChoiceCards = 0
  multiChoiceCardsLoading = false

  showMultiChoiceCardDialog = false
  showExtendDialog = false
  showRevokeDialog = false
  showBulkRevokeDialog = false
  extendDialogCards: MultiChoiceCard[] = []
  bulkRevokeCards: MultiChoiceCard[] = []
  selectedCard: MultiChoiceCard | null = null
  showResendEmailDialog = false
  resendEmailGift: Gift | null = null
  pdfLoadingGiftId: string | null = null
  selectedCards: MultiChoiceCard[] = []
  clipboardSuccess = clipboardSuccess
  private eligibleGiftCardsByMcgcId: Record<string, GiftCard[]> = {}
  private eligibleGiftCardsLoading: Record<string, boolean> = {}

  filters: any = {
    email: null,
    firstName: null,
    lastName: null,
    dateRange: null,
    amount: null
  }
  private pagination = {
    page: 1,
    limit: 20
  }

  get selectedExtendableCards(): MultiChoiceCard[] {
    return this.selectedCards.filter(card => card.canExtendExpiration)
  }

  get selectedRevocableCards(): MultiChoiceCard[] {
    return this.selectedCards.filter(card => card.canRevoke)
  }

  formatDate(date: string | Date): string {
    const formattedDate = moment(date).format('L')
    return formattedDate === 'Invalid date' ? '' : formattedDate
  }

  emailStatus(row: MultiChoiceCard): string {
    const gift = row.gift
    if (!gift) {
      return ''
    }
    if (gift.emailFailedAt) {
      return `Échec d'envoi`
    }
    if (gift.emailOpenedAt) {
      return `Ouvert le ${moment(gift.emailOpenedAt).format('L [à] LT')}`
    }
    if (gift.emailDeliveredAt) {
      return `Reçu le ${moment(gift.emailDeliveredAt).format('L [à] LT')}`
    }
    return 'Envoyé'
  }

  formatScheduledAt(date: string | null): string {
    if (!date) return ''
    if (moment(date).format('YYYY-MM-DD') === moment().format('YYYY-MM-DD')) return ''
    return this.formatDate(date)
  }

  extendTooltip(row: MultiChoiceCard): string {
    if (row.isBlankCard) {
      return String(this.$t('gifts.extendDialog.notExtendableBlankTooltip'))
    }
    if (!row.canExtendExpiration) {
      return String(this.$t('gifts.extendDialog.notExtendableTooltip'))
    }
    return String(this.$t('gifts.extendDialog.tooltip'))
  }

  revokeTooltip(row: MultiChoiceCard): string {
    if (!row.canRevoke) {
      return String(this.$t('gifts.revokeDialog.notRevocableUsedTooltip'))
    }
    return String(this.$t('gifts.revokeDialog.tooltip'))
  }

  hasGiftDetail(row: MultiChoiceCard): boolean {
    return !!row.multiChoiceGiftCard?.name
  }

  eligibleBrandsCount(row: MultiChoiceCard): number {
    const giftCards = this.eligibleGiftCards(row)
    if (giftCards.length > 0) return giftCards.length
    return row.multiChoiceGiftCard?.eligibleGiftCardsCount || 0
  }

  eligibleGiftCards(row: MultiChoiceCard): GiftCard[] {
    const mcgcId = row.multiChoiceGiftCard?.id
    if (mcgcId && this.eligibleGiftCardsByMcgcId[mcgcId]?.length) {
      return this.eligibleGiftCardsByMcgcId[mcgcId]
    }
    return row.multiChoiceGiftCard?.giftCards || []
  }

  isEligibleBrandsLoading(row: MultiChoiceCard): boolean {
    const mcgcId = row.multiChoiceGiftCard?.id
    return mcgcId ? !!this.eligibleGiftCardsLoading[mcgcId] : false
  }

  groupGiftCardsByLetter(giftCards: GiftCard[]): Record<string, GiftCard[]> {
    const groups: Record<string, GiftCard[]> = {}

    ;[...giftCards]
      .sort((a, b) => a.name.localeCompare(b.name, 'fr', { sensitivity: 'base' }))
      .forEach((giftCard) => {
        const letter = (giftCard.name.charAt(0) || '#').toUpperCase()
        if (!groups[letter]) groups[letter] = []
        groups[letter].push(giftCard)
      })

    return Object.keys(groups)
      .sort((a, b) => a.localeCompare(b, 'fr', { sensitivity: 'base' }))
      .reduce((sortedGroups, letter) => {
        sortedGroups[letter] = groups[letter]
        return sortedGroups
      }, {} as Record<string, GiftCard[]>)
  }

  async loadEligibleGiftCards(row: MultiChoiceCard): Promise<void> {
    const mcgcId = row.multiChoiceGiftCard?.id
    if (!mcgcId || this.eligibleGiftCardsByMcgcId[mcgcId] || this.eligibleGiftCardsLoading[mcgcId]) return

    if (row.multiChoiceGiftCard.giftCards?.length) {
      this.$set(this.eligibleGiftCardsByMcgcId, mcgcId, row.multiChoiceGiftCard.giftCards)
      return
    }

    this.$set(this.eligibleGiftCardsLoading, mcgcId, true)
    try {
      const { data } = await MultiChoiceGiftCard.includes('giftCards').find(mcgcId)
      this.$set(this.eligibleGiftCardsByMcgcId, mcgcId, data.giftCards || [])
    } finally {
      this.$set(this.eligibleGiftCardsLoading, mcgcId, false)
    }
  }

  openExtendDialog(card: MultiChoiceCard): void {
    this.extendDialogCards = [card]
    this.showExtendDialog = true
  }

  openBulkExtendDialog(): void {
    this.extendDialogCards = [...this.selectedExtendableCards]
    this.showExtendDialog = true
  }

  closeExtendDialog(): void {
    this.showExtendDialog = false
    this.extendDialogCards = []
  }

  handleExtended(updatedCards?: MultiChoiceCard[]): void {
    if (updatedCards?.length) {
      updatedCards.forEach(updated => {
        const index = this.multiChoiceCards.findIndex(card => card.id === updated.id)
        if (index < 0) return

        const existing = this.multiChoiceCards[index]
        updated.customer = existing.customer
        updated.multiChoiceGiftCard = existing.multiChoiceGiftCard
        this.$set(this.multiChoiceCards, index, updated)
      })
      return
    }

    this.clearSelection()
    this.getMultiChoiceCards()
  }

  openRevokeDialog(card: MultiChoiceCard): void {
    this.selectedCard = card
    this.showRevokeDialog = true
  }

  closeRevokeDialog(): void {
    this.showRevokeDialog = false
    this.selectedCard = null
  }

  canShowResendEmail(row: MultiChoiceCard): boolean {
    if (!row.gift) {
      return false
    }
    return !!row.gift.emailFailedAt
  }

  resendLimitReached(row: MultiChoiceCard): boolean {
    const count = row.gift?.emailResendCount || 0
    return count >= Gift.MAX_EMAIL_RESENDS
  }

  resendEmailTooltip(row: MultiChoiceCard): string {
    if (this.resendLimitReached(row)) {
      return String(this.$t('gifts.resendEmailDialog.limitReachedTooltip'))
    }
    return String(this.$t('gifts.resendEmailDialog.actionTooltip'))
  }

  openResendEmailDialog(row: MultiChoiceCard): void {
    this.resendEmailGift = row.gift
    this.showResendEmailDialog = true
  }

  closeResendEmailDialog(): void {
    this.showResendEmailDialog = false
    this.resendEmailGift = null
  }

  canShowRedemptionPdf(row: MultiChoiceCard): boolean {
    if (!row.gift) {
      return false
    }
    if (row.gift.redemptionCodeGeneratedAt) {
      return true
    }
    return !!row.gift.emailFailedAt
  }

  redemptionPdfTooltip(row: MultiChoiceCard): string {
    if (row.gift && row.gift.redemptionCodeGeneratedAt) {
      return String(this.$t('gifts.redemptionPdf.downloadTooltip'))
    }
    return String(this.$t('gifts.redemptionPdf.generateTooltip'))
  }

  async downloadRedemptionPdf(row: MultiChoiceCard): Promise<void> {
    if (!row.gift) {
      return
    }
    const alreadyGenerated = !!row.gift.redemptionCodeGeneratedAt

    this.pdfLoadingGiftId = row.gift.id
    try {
      await Gift.downloadRedemptionPdf(row.gift.id)
      if (!alreadyGenerated) {
        this.getMultiChoiceCards()
      }
    } catch (err) {
      this.$notify({
        title: String(this.$t('gifts.redemptionPdf.notification.error.title')),
        message: String(this.$t('gifts.redemptionPdf.notification.error.fallback')),
        type: 'error',
        duration: 5000
      })
    } finally {
      this.pdfLoadingGiftId = null
    }
  }

  openBulkRevokeDialog(): void {
    this.bulkRevokeCards = [...this.selectedRevocableCards]
    this.showBulkRevokeDialog = true
  }

  closeBulkRevokeDialog(): void {
    this.showBulkRevokeDialog = false
    this.bulkRevokeCards = []
  }

  isRowSelectable(row: MultiChoiceCard): boolean {
    return row.canExtendExpiration || row.canRevoke
  }

  rowClassName({ row }: { row: MultiChoiceCard }): string {
    return row.canExtendExpiration || row.canRevoke ? '' : 'row-not-actionable'
  }

  handleSelectionChange(selection: MultiChoiceCard[]): void {
    this.selectedCards = selection
  }

  clearSelection(): void {
    this.selectedCards = []
    ;(this.$refs.multiChoiceCardsTable as Table)?.clearSelection()
  }

  handleRevoked(): void {
    this.clearSelection()
    this.getMultiChoiceCards()
    this.$emit('revoked')
  }

  @Watch('operation', { immediate: true })
  async getMultiChoiceCards() {
    this.multiChoiceCardsLoading = true

    const createdAtGte = this.filters.dateRange ? moment(String(this.filters.dateRange![0])).format('YYYY-MM-DD HH:mm:ss') : ''
    const createdAtLte = this.filters.dateRange ? moment(String(this.filters.dateRange![1])).format('YYYY-MM-DD HH:mm:ss') : ''

    const { data, meta } =
      await MultiChoiceCard
        .includes(['customer', 'multiChoiceGiftCard', { gift: 'recipient' }])
        .where({
          dotationOperationId: this.operation.id,
          customerEmail: { prefix: this.filters.email },
          customerFirstname: { prefix: this.filters.firstName },
          customerLastname: { prefix: this.filters.lastName },
          amountCents: this.filters.amount ? this.filters.amount * 100 : null,
          createdAt: { gte: createdAtGte, lte: createdAtLte }
        })
        .page(this.pagination.page)
        .per(this.pagination.limit)
        .order({ createdAt: 'desc' })
        .stats({ total: 'count' })
        .all()

    if (data.length === 0 && this.pagination.page > 1) this.pagination.page -= 1
    else this.multiChoiceCards = data

    this.clearSelection()
    this.multiChoiceCardsLoading = false
    this.totalMultiChoiceCards = meta.stats.total.count
  }

  handleSearch(filters: any): void {
    this.filters = filters
    this.pagination.page = 1
    this.multiChoiceCards = []
    this.getMultiChoiceCards()
  }

  truncateID(id: string): string {
    return `${id.slice(0, 4)} .... ${id.substr(id.length - 4)}`
  }

  addInList(multiChoiceCard: MultiChoiceCard): void {
    this.multiChoiceCards.unshift(multiChoiceCard)
  }
}
